import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GripVertical, ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, RotateCcw, Save, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductOrderManager = ({ categories, token, onClose }) => {
    const [categoryId, setCategoryId] = useState('__global__');
    const [subcategory, setSubcategory] = useState('');
    const [brandId, setBrandId] = useState('');
    const [catBrands, setCatBrands] = useState([]);
    const [items, setItems] = useState([]);
    const [savedItems, setSavedItems] = useState([]);
    const [scopeKey, setScopeKey] = useState('global');
    const [version, setVersion] = useState(0);
    const [hasManualOrder, setHasManualOrder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);
    const listRef = useRef(null);
    const autoScrollRef = useRef(null);

    const selectedCat = categories.find(c => c.category_id === categoryId);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (categoryId !== '__global__') params.category_id = categoryId;
            if (subcategory) params.subcategory = subcategory;
            if (brandId) params.brand_id = brandId;
            const res = await axios.get(`${API}/admin/product-order/items`, {
                params, headers: { Authorization: `Bearer ${token}` }
            });
            setItems(res.data.items);
            setSavedItems(res.data.items);
            setScopeKey(res.data.scope_key);
            setVersion(res.data.version);
            setHasManualOrder(res.data.has_manual_order);
            setDirty(false);
        } catch {
            toast.error('Грешка при зареждане на продуктите');
        } finally {
            setLoading(false);
        }
    }, [categoryId, subcategory, brandId, token]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    // Brands available in the selected category (for brand-scope ordering)
    useEffect(() => {
        if (categoryId === '__global__' || !selectedCat?.organize_by_brands || !selectedCat?.slug) {
            setCatBrands([]);
            setBrandId('');
            return;
        }
        axios.get(`${API}/categories/${encodeURIComponent(selectedCat.slug)}/brands`)
            .then(res => setCatBrands((res.data.brands || []).filter(b => b.count > 0)))
            .catch(() => setCatBrands([]));
    }, [categoryId, selectedCat]);

    const applyMove = (from, to) => {
        if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
        const next = [...items];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setItems(next);
        setDirty(true);
    };

    const moveToPosition = (index, posStr) => {
        const pos = parseInt(posStr, 10);
        if (isNaN(pos)) return;
        applyMove(index, Math.min(Math.max(pos - 1, 0), items.length - 1));
    };

    // Pointer-based drag & drop (works with mouse and touch)
    const startDrag = (e, index) => {
        e.preventDefault();
        setDragIndex(index);
        setOverIndex(index);
        const move = (ev) => {
            const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
            // Auto-scroll near viewport edges
            if (y < 120) window.scrollBy({ top: -18 });
            else if (y > window.innerHeight - 120) window.scrollBy({ top: 18 });
            const rows = listRef.current?.querySelectorAll('[data-order-row]');
            if (!rows) return;
            let target = null;
            rows.forEach((row, i) => {
                const r = row.getBoundingClientRect();
                if (y >= r.top && y <= r.bottom) target = i;
            });
            if (target !== null) setOverIndex(target);
            else if (rows.length && y > rows[rows.length - 1].getBoundingClientRect().bottom) setOverIndex(rows.length - 1);
        };
        const up = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            setDragIndex(prev => {
                setOverIndex(prevOver => {
                    if (prev !== null && prevOver !== null && prev !== prevOver) {
                        setItems(cur => {
                            const next = [...cur];
                            const [moved] = next.splice(prev, 1);
                            next.splice(prevOver, 0, moved);
                            return next;
                        });
                        setDirty(true);
                    }
                    return null;
                });
                return null;
            });
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        clearInterval(autoScrollRef.current);
    };

    const saveOrder = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/admin/product-order`, {
                scope_key: scopeKey,
                product_ids: items.map(i => i.product_id),
                version
            }, { headers: { Authorization: `Bearer ${token}` } });
            setVersion(res.data.version);
            setSavedItems(items);
            setDirty(false);
            setHasManualOrder(true);
            toast.success('Подредбата е запазена успешно');
        } catch (e) {
            setItems(savedItems);
            setDirty(false);
            if (e.response?.status === 409) {
                toast.error(e.response.data.detail);
                await fetchItems();
            } else {
                toast.error('Грешка при запазване — редът е върнат към последния запазен.');
            }
        } finally {
            setSaving(false);
        }
    };

    const resetOrder = async () => {
        if (!window.confirm('Сигурни ли сте, че искате да нулирате ръчния ред за този изглед? Продуктите ще се върнат към автоматичното подреждане.')) return;
        try {
            await axios.delete(`${API}/admin/product-order`, {
                params: { scope_key: scopeKey }, headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ръчният ред е нулиран');
            await fetchItems();
        } catch {
            toast.error('Грешка при нулиране');
        }
    };

    const scopeLabel = categoryId === '__global__'
        ? 'Глобален ред (всички продукти)'
        : `${selectedCat?.name || ''}${subcategory ? ` → ${subcategory}` : ''}${brandId ? ` → ${catBrands.find(b => b.brand_id === brandId)?.name || ''}` : ''}`;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-6" data-testid="product-order-manager">
            <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-4xl mx-4 shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b dark:border-slate-700 p-4 rounded-t-xl">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-serif text-xl font-bold dark:text-white">Ръчно подреждане на продукти</h2>
                        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-order-manager-btn"><X className="h-5 w-5 dark:text-slate-300" /></Button>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <div>
                            <Label className="text-xs dark:text-slate-400">Обхват</Label>
                            <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setSubcategory(''); setBrandId(''); }}>
                                <SelectTrigger className="w-56 dark:bg-slate-700 dark:border-slate-600" data-testid="order-scope-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="dark:bg-slate-800">
                                    <SelectItem value="__global__">🌐 Глобално (всички продукти)</SelectItem>
                                    {categories.filter(c => c.category_id).map(c => (
                                        <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {selectedCat?.subcategories?.length > 0 && (
                            <div>
                                <Label className="text-xs dark:text-slate-400">Подкатегория</Label>
                                <Select value={subcategory || '__all__'} onValueChange={(v) => { setSubcategory(v === '__all__' ? '' : v); setBrandId(''); }}>
                                    <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600" data-testid="order-scope-subcategory-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="dark:bg-slate-800">
                                        <SelectItem value="__all__">Цялата категория</SelectItem>
                                        {selectedCat.subcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {catBrands.length > 0 && !subcategory && (
                            <div>
                                <Label className="text-xs dark:text-slate-400">Марка</Label>
                                <Select value={brandId || '__all__'} onValueChange={(v) => setBrandId(v === '__all__' ? '' : v)}>
                                    <SelectTrigger className="w-44 dark:bg-slate-700 dark:border-slate-600" data-testid="order-scope-brand-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="dark:bg-slate-800">
                                        <SelectItem value="__all__">Всички марки</SelectItem>
                                        {catBrands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name} ({b.count})</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                        <div className="text-xs text-muted-foreground dark:text-slate-400">
                            {scopeLabel} • {items.length} продукта {hasManualOrder && <Badge variant="secondary" className="ml-1 text-[10px]">има ръчен ред</Badge>}
                        </div>
                        <div className="flex gap-2">
                            {hasManualOrder && (
                                <Button variant="outline" size="sm" onClick={resetOrder} className="dark:border-slate-600 dark:text-white" data-testid="reset-order-btn">
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Нулирай ръчния ред
                                </Button>
                            )}
                            <Button size="sm" onClick={saveOrder} disabled={!dirty || saving} className="btn-primary" data-testid="save-order-btn">
                                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                                Запази подредбата
                            </Button>
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="p-4" ref={listRef}>
                    {loading ? (
                        <div className="py-16 text-center text-muted-foreground dark:text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Зареждане...</div>
                    ) : items.length === 0 ? (
                        <p className="py-16 text-center text-muted-foreground dark:text-slate-400">Няма продукти в този изглед</p>
                    ) : (
                        <div className="space-y-1">
                            {items.map((item, index) => (
                                <div
                                    key={item.product_id}
                                    data-order-row
                                    data-testid={`order-row-${item.product_id}`}
                                    className={`flex items-center gap-3 p-2 rounded-lg border transition-all select-none ${
                                        dragIndex === index
                                            ? 'opacity-40 border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                                            : overIndex === index && dragIndex !== null
                                                ? 'border-amber-500 border-t-4 bg-amber-50/50 dark:bg-amber-900/10'
                                                : 'border-transparent bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700'
                                    }`}
                                >
                                    <button
                                        onPointerDown={(e) => startDrag(e, index)}
                                        className="cursor-grab active:cursor-grabbing p-1.5 rounded hover:bg-amber-100 dark:hover:bg-slate-600 touch-none"
                                        title="Хвани и премести"
                                        data-testid={`drag-handle-${item.product_id}`}
                                        style={{ touchAction: 'none' }}
                                    >
                                        <GripVertical className="h-5 w-5 text-slate-400" />
                                    </button>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={items.length}
                                        value={index + 1}
                                        onChange={(e) => moveToPosition(index, e.target.value)}
                                        className="w-16 h-8 text-center text-sm dark:bg-slate-700 dark:border-slate-600"
                                        data-testid={`position-input-${item.product_id}`}
                                    />
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${item.thumbnail_url}`} alt="" className="h-10 w-10 rounded object-cover bg-white border dark:border-slate-600" loading="lazy" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate dark:text-white">{item.name || '(без име)'}</p>
                                        <p className="text-xs text-muted-foreground dark:text-slate-400">
                                            {item.price != null ? `${item.price.toFixed(2)} €` : '—'}
                                            {item.brand ? ` • ${item.brand}` : ''}
                                            {item.subcategory ? ` • ${item.subcategory}` : ''}
                                            {item.is_visible === false ? ' • скрит' : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => applyMove(index, 0)} disabled={index === 0} title="Премести най-отгоре" data-testid={`move-top-${item.product_id}`}><ChevronsUp className="h-4 w-4 dark:text-slate-300" /></Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => applyMove(index, index - 1)} disabled={index === 0} title="Една позиция нагоре" data-testid={`move-up-${item.product_id}`}><ArrowUp className="h-4 w-4 dark:text-slate-300" /></Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => applyMove(index, index + 1)} disabled={index === items.length - 1} title="Една позиция надолу" data-testid={`move-down-${item.product_id}`}><ArrowDown className="h-4 w-4 dark:text-slate-300" /></Button>
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => applyMove(index, items.length - 1)} disabled={index === items.length - 1} title="Премести най-отдолу" data-testid={`move-bottom-${item.product_id}`}><ChevronsDown className="h-4 w-4 dark:text-slate-300" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {dirty && (
                    <div className="sticky bottom-0 bg-amber-50 dark:bg-amber-900/30 border-t border-amber-200 dark:border-amber-700 p-3 rounded-b-xl flex items-center justify-between">
                        <p className="text-sm text-amber-800 dark:text-amber-300">Има незапазени промени в подредбата</p>
                        <Button size="sm" onClick={saveOrder} disabled={saving} className="btn-primary" data-testid="save-order-footer-btn">
                            {saving ? 'Запазване...' : 'Запази подредбата'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductOrderManager;
